import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  FlaskConical, TrendingUp, CreditCard, Clock, MapPin, RefreshCw,
  CalendarCheck, Package, Activity, Eye, Users, Smartphone, ArrowRight, ArrowDown,
} from "lucide-react";
import { getProviderMeta } from "../../../../utils/labProviders";

const API_BASE = "https://api.medstown.com";
const EVENTS_API = `${API_BASE}/api/events`;

// Lab screens tracked in the Medstown mobile app
const LAB_SCREENS = [
  { key: "Lab Test Home",            label: "Lab Home",          step: 1 },
  { key: "Lab Test Detail",          label: "Test Detail",       step: 2 },
  { key: "Lab Test Cart",            label: "Cart",              step: 3 },
  { key: "Lab Test Patient Details", label: "Patient Details",   step: 4 },
  { key: "Lab Test Slot Booking",    label: "Slot Booking",      step: 5 },
  { key: "Lab Test Booking Confirm", label: "Booking Confirm",   step: 6 },
];

const inr = (n) =>
  "\u20B9" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const inrShort = (n) => {
  const v = Number(n || 0);
  if (v >= 1e7) return `\u20B9${(v / 1e7).toFixed(1)}Cr`;
  if (v >= 1e5) return `\u20B9${(v / 1e5).toFixed(1)}L`;
  if (v >= 1e3) return `\u20B9${(v / 1e3).toFixed(1)}k`;
  return `\u20B9${v}`;
};

const STATUS_COLORS = {
  Scheduled: "#4f46e5", Confirmed: "#0284c7", Assigned: "#0891b2",
  SampleCollected: "#7c3aed", ReportGenerated: "#059669", Completed: "#059669",
  Rescheduled: "#d97706", CancellationRequested: "#d97706", Cancelled: "#dc2626",
};

const FUNNEL_COLORS = ["#4f46e5", "#6366f1", "#7c3aed", "#8b5cf6", "#059669", "#10b981"];

// ─── Reusable primitives ──────────────────────────────────────────────────
const S = {
  card: {
    backgroundColor: "#fff", borderRadius: "16px", padding: "24px",
    border: "1px solid #e2e8f0", boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
  },
  th: {
    padding: "10px 14px", fontSize: "12px", fontWeight: "700",
    color: "#4a5568", borderBottom: "2px solid #e2e8f0", textAlign: "left",
  },
  td: { padding: "10px 14px", fontSize: "13px", color: "#2d3748" },
  badge: (c) => ({
    display: "inline-block", padding: "4px 10px",
    backgroundColor: `${c}18`, color: c,
    borderRadius: "6px", fontSize: "12px", fontWeight: "700",
  }),
};

const SectionCard = ({ title, icon: Icon, color, action, children }) => (
  <div style={S.card}>
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      marginBottom: "20px", paddingBottom: "14px", borderBottom: "2px solid #f7fafc",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <div style={{
          width: "36px", height: "36px", backgroundColor: `${color}18`,
          borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Icon size={20} color={color} />
        </div>
        <h3 style={{ fontSize: "16px", fontWeight: "700", color: "#1a202c", margin: 0 }}>{title}</h3>
      </div>
      {action}
    </div>
    {children}
  </div>
);

const KpiCard = ({ icon: Icon, label, value, sub, color, loading }) => (
  <div style={{
    ...S.card, padding: "18px 20px", transition: "all 0.25s", cursor: "default",
  }}
    onMouseOver={(e) => { e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.08)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
    onMouseOut={(e) => { e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.04)"; e.currentTarget.style.transform = "translateY(0)"; }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
      <div style={{
        width: "44px", height: "44px", backgroundColor: `${color}15`,
        borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Icon size={22} color={color} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: "12px", color: "#718096", fontWeight: "600", marginBottom: "2px" }}>{label}</div>
        {loading ? (
          <div style={{ width: "60px", height: "24px", backgroundColor: "#e2e8f0", borderRadius: "4px", animation: "pulse 1.5s infinite" }} />
        ) : (
          <div style={{ fontSize: "22px", fontWeight: "800", color: "#1a202c", lineHeight: 1.1 }}>{value}</div>
        )}
        {sub && <div style={{ fontSize: "11px", color: "#718096", marginTop: "2px" }}>{sub}</div>}
      </div>
    </div>
  </div>
);

const Skeleton = ({ h = 56, count = 3 }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
    {Array(count).fill(0).map((_, i) => (
      <div key={i} style={{ height: `${h}px`, backgroundColor: "#e2e8f0", borderRadius: "12px", animation: "pulse 1.5s infinite" }} />
    ))}
  </div>
);

const Empty = ({ text = "No data available" }) => (
  <div style={{ textAlign: "center", color: "#a0aec0", padding: "40px", fontSize: "14px" }}>{text}</div>
);

// ─── Funnel Step ──────────────────────────────────────────────────────────
const FunnelBar = ({ step, label, visits, users, maxVisits, color, dropPct }) => {
  const barW = maxVisits > 0 ? (visits / maxVisits) * 100 : 0;
  return (
    <div>
      {dropPct !== null && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "2px 0", gap: "4px" }}>
          <ArrowDown size={12} color="#e53e3e" />
          <span style={{ fontSize: "11px", color: "#e53e3e", fontWeight: "600" }}>-{dropPct}% drop-off</span>
        </div>
      )}
      <div style={{
        display: "flex", alignItems: "center", gap: "10px", padding: "12px 14px",
        backgroundColor: visits > 0 ? "#f7fafc" : "#fafafa",
        borderRadius: "10px", border: `1px solid ${visits > 0 ? "#e2e8f0" : "#f0f0f0"}`,
        opacity: visits > 0 ? 1 : 0.55, transition: "all 0.2s",
      }}>
        <span style={{
          width: "26px", height: "26px",
          background: visits > 0 ? color : "#cbd5e0",
          color: "#fff", borderRadius: "7px", display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "11px", fontWeight: "700", flexShrink: 0,
        }}>{step}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "5px" }}>
            <span style={{ fontWeight: "600", color: "#2d3748", fontSize: "13px" }}>{label}</span>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "3px", fontSize: "11px", color: "#718096", fontWeight: "600" }}>
                <Users size={11} />{users}
              </span>
              <span style={{ fontWeight: "700", color: "#2d3748", fontSize: "15px", fontVariantNumeric: "tabular-nums" }}>{visits.toLocaleString()}</span>
            </div>
          </div>
          <div style={{ width: "100%", height: "5px", backgroundColor: "#e2e8f0", borderRadius: "3px", overflow: "hidden" }}>
            <div style={{ width: `${barW}%`, height: "100%", background: color, borderRadius: "3px", transition: "width 0.5s ease" }} />
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Status Badge ─────────────────────────────────────────────────────────
const StatusRow = ({ status, count, revenue, total }) => {
  const c = STATUS_COLORS[status] || "#718096";
  const pct = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "10px 14px", backgroundColor: "#f7fafc", borderRadius: "10px",
      border: "1px solid #e2e8f0", transition: "all 0.2s",
    }}
      onMouseOver={(e) => { e.currentTarget.style.backgroundColor = `${c}0a`; e.currentTarget.style.borderColor = `${c}30`; }}
      onMouseOut={(e) => { e.currentTarget.style.backgroundColor = "#f7fafc"; e.currentTarget.style.borderColor = "#e2e8f0"; }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: c }} />
        <div>
          <div style={{ fontWeight: "600", color: "#2d3748", fontSize: "13px" }}>{status}</div>
          <div style={{ fontSize: "11px", color: "#718096" }}>{pct}%</div>
        </div>
      </div>
      <div style={{ textAlign: "right" }}>
        <div style={{ fontWeight: "700", color: "#2d3748", fontSize: "15px" }}>{count}</div>
        <div style={{ fontSize: "11px", color: "#718096" }}>{inrShort(revenue)}</div>
      </div>
    </div>
  );
};

// ─── Provider Row ─────────────────────────────────────────────────────────
const ProviderRow = ({ provider, count, revenue }) => {
  const m = getProviderMeta(provider);
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "12px 14px", backgroundColor: "#f7fafc", borderRadius: "10px", border: "1px solid #e2e8f0",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <div style={{ width: "5px", height: "28px", borderRadius: "3px", backgroundColor: m.tone }} />
        <div>
          <div style={{ fontWeight: "600", color: "#2d3748", fontSize: "13px" }}>{m.label}</div>
          <div style={{ fontSize: "11px", color: "#718096" }}>{count} orders</div>
        </div>
      </div>
      <div style={{ fontWeight: "700", color: "#2d3748", fontSize: "15px", fontVariantNumeric: "tabular-nums" }}>{inrShort(revenue)}</div>
    </div>
  );
};

// ─── Pincode Row ──────────────────────────────────────────────────────────
const PincodeRow = ({ rank, pincode, count, maxCount }) => {
  const barW = maxCount > 0 ? (count / maxCount) * 100 : 0;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: "10px", padding: "8px 12px",
      backgroundColor: "#f7fafc", borderRadius: "10px", border: "1px solid #e2e8f0",
    }}>
      <span style={{
        width: "24px", height: "24px", backgroundColor: "#4f46e5", color: "#fff",
        borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: "11px", fontWeight: "700", flexShrink: 0,
      }}>{rank}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
          <span style={{ fontWeight: "600", color: "#2d3748", fontSize: "13px", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>{pincode || "N/A"}</span>
          <span style={{ fontWeight: "700", color: "#2d3748", fontSize: "13px" }}>{count}</span>
        </div>
        <div style={{ width: "100%", height: "3px", backgroundColor: "#e2e8f0", borderRadius: "2px", overflow: "hidden" }}>
          <div style={{ width: `${barW}%`, height: "100%", background: "linear-gradient(90deg,#4f46e5,#7c3aed)", borderRadius: "2px", transition: "width 0.5s" }} />
        </div>
      </div>
    </div>
  );
};

// ─── Trend bar chart ──────────────────────────────────────────────────────
const TrendBars = ({ data, loading }) => {
  const maxC = useMemo(() => (data || []).reduce((m, d) => Math.max(m, d.count || 0), 1), [data]);
  if (loading) return <Skeleton h={30} count={7} />;
  if (!data || data.length === 0) return <Empty text="No trend data" />;
  const display = data.slice(-14);
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${display.length}, 1fr)`, gap: "6px", height: "220px", alignItems: "flex-end", padding: "12px 4px" }}>
      {display.map((p) => {
        const h = Math.max((p.count / maxC) * 190, 20);
        return (
          <div key={p._id} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
            <div style={{ fontSize: "11px", fontWeight: "700", color: "#2d3748" }}>{p.count}</div>
            <div
              style={{ width: "100%", height: `${h}px`, background: "linear-gradient(180deg,#4f46e5,#7c3aed)", borderRadius: "8px", boxShadow: "0 2px 8px rgba(79,70,229,0.2)", transition: "transform 0.2s" }}
              onMouseOver={(e) => e.currentTarget.style.transform = "scaleY(1.06)"}
              onMouseOut={(e) => e.currentTarget.style.transform = "scaleY(1)"}
              title={`${p.count} orders | ${inr(p.revenue)}`}
            />
            <div style={{ fontSize: "9px", color: "#718096", fontWeight: "600", textAlign: "center" }}>
              {new Date(p._id).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Donut ────────────────────────────────────────────────────────────────
const StatusDonut = ({ data, loading }) => {
  const total = (data || []).reduce((s, r) => s + r.count, 0);
  const sorted = (data || []).slice().sort((a, b) => b.count - a.count);
  const segments = useMemo(() => {
    if (!total) return [];
    let acc = 0;
    return sorted.map((r) => {
      const pct = (r.count / total) * 100;
      const tone = STATUS_COLORS[r._id] || "#718096";
      const seg = { key: r._id || "unknown", tone, pct, count: r.count, start: acc, end: acc + pct };
      acc += pct;
      return seg;
    });
  }, [sorted, total]);

  const gradient = segments.length
    ? `conic-gradient(${segments.map((s) => `${s.tone} ${s.start}% ${s.end}%`).join(", ")})`
    : `conic-gradient(#e2e8f0 0% 100%)`;

  if (loading) return <div style={{ display: "flex", justifyContent: "center", padding: "30px" }}><div style={{ width: "120px", height: "120px", borderRadius: "50%", backgroundColor: "#e2e8f0", animation: "pulse 1.5s infinite" }} /></div>;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap", justifyContent: "center" }}>
      <div style={{ width: "120px", height: "120px", borderRadius: "50%", background: gradient, position: "relative", flexShrink: 0 }}>
        <div style={{ position: "absolute", inset: "16px", borderRadius: "50%", backgroundColor: "#fff", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", border: "1px solid #f1f5f9" }}>
          <div style={{ fontSize: "20px", fontWeight: "800", color: "#1a202c", lineHeight: 1 }}>{total}</div>
          <div style={{ fontSize: "10px", color: "#718096", textTransform: "uppercase", letterSpacing: "0.05em", marginTop: "2px", fontWeight: "600" }}>Total</div>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: 1, minWidth: "160px" }}>
        {segments.map((s) => (
          <div key={s.key} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <div style={{ width: "7px", height: "7px", borderRadius: "50%", backgroundColor: s.tone, flexShrink: 0 }} />
            <span style={{ fontSize: "12px", color: "#2d3748", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.key}</span>
            <span style={{ fontSize: "12px", color: "#718096", fontWeight: "600", fontVariantNumeric: "tabular-nums" }}>{s.count} ({s.pct.toFixed(0)}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Screen Users Table ───────────────────────────────────────────────────
const PAGE_SIZE = 20;

const ScreenUsersSection = ({ range }) => {
  const [selectedScreen, setSelectedScreen] = useState("Lab Test Home");
  const [screenUsers, setScreenUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [page, setPage] = useState(1);

  const fetchScreenUsers = useCallback(async () => {
    setLoadingUsers(true);
    setPage(1);
    try {
      const params = new URLSearchParams({ screen: selectedScreen, range });
      const res = await fetch(`${EVENTS_API}/users/by-screen?${params}`);
      const json = await res.json();
      setScreenUsers(json.data?.users || []);
    } catch (err) { console.error("Screen users error:", err); }
    finally { setLoadingUsers(false); }
  }, [selectedScreen, range]);

  useEffect(() => { fetchScreenUsers(); }, [fetchScreenUsers]);

  const totalVisits = screenUsers.reduce((s, u) => s + u.visitCount, 0);
  const totalPages = Math.ceil(screenUsers.length / PAGE_SIZE);
  const paged = screenUsers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <SectionCard title="Lab Screen Users" icon={Eye} color="#7c3aed">
      {/* Screen pills */}
      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "18px" }}>
        {LAB_SCREENS.map((s) => (
          <button key={s.key} onClick={() => setSelectedScreen(s.key)} style={{
            padding: "7px 14px", fontSize: "12px", fontWeight: "600", border: "none", borderRadius: "8px",
            cursor: "pointer", transition: "all 0.2s",
            backgroundColor: selectedScreen === s.key ? "#4f46e5" : "#f7fafc",
            color: selectedScreen === s.key ? "#fff" : "#4a5568",
            boxShadow: selectedScreen === s.key ? "0 3px 10px rgba(79,70,229,0.25)" : "none",
            border: selectedScreen === s.key ? "none" : "1px solid #e2e8f0",
          }}>
            {s.label}
          </button>
        ))}
      </div>

      {/* Mini stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px", marginBottom: "18px" }}>
        {[
          { v: loadingUsers ? "..." : screenUsers.length, l: "Users", bg: "#eef2ff", c: "#4f46e5" },
          { v: loadingUsers ? "..." : totalVisits, l: "Total Visits", bg: "#ecfdf5", c: "#059669" },
          { v: loadingUsers ? "..." : (screenUsers.length > 0 ? Math.round(totalVisits / screenUsers.length) : 0), l: "Avg/User", bg: "#fffbeb", c: "#d97706" },
        ].map((m) => (
          <div key={m.l} style={{ padding: "12px", backgroundColor: m.bg, borderRadius: "10px", textAlign: "center" }}>
            <div style={{ fontSize: "20px", fontWeight: "800", color: m.c }}>{m.v}</div>
            <div style={{ fontSize: "10px", color: "#718096", fontWeight: "600", marginTop: "2px" }}>{m.l}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead style={{ position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 1 }}>
            <tr style={{ backgroundColor: "#f7fafc" }}>
              {["User", "Phone", "Pincode", "Visits", "First Visit", "Last Visit"].map((h) => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loadingUsers ? (
              <tr><td colSpan="6" style={{ padding: "36px", textAlign: "center" }}><RefreshCw size={22} className="spin" color="#4f46e5" /></td></tr>
            ) : screenUsers.length === 0 ? (
              <tr><td colSpan="6" style={{ padding: "36px", textAlign: "center", color: "#a0aec0", fontSize: "13px" }}>No users found for this screen</td></tr>
            ) : (
              paged.map((u, i) => (
                <tr key={u.userId || i} style={{ borderBottom: "1px solid #e2e8f0", transition: "background 0.15s" }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = "#f7fafc"}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                >
                  <td style={S.td}><span style={{ fontWeight: "600" }}>{u.fullName || "Anonymous"}</span></td>
                  <td style={S.td}>{u.phone || "N/A"}</td>
                  <td style={S.td}>{u.geoInfo?.pincode || "N/A"}</td>
                  <td style={S.td}><span style={S.badge("#4f46e5")}>{u.visitCount}</span></td>
                  <td style={S.td}><span style={{ fontSize: "11px", color: "#718096" }}>{new Date(u.firstVisit).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span></td>
                  <td style={S.td}><span style={{ fontSize: "11px", color: "#718096" }}>{new Date(u.lastVisit).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          marginTop: "16px", display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "12px 14px", backgroundColor: "#f7fafc", borderRadius: "10px",
        }}>
          <div style={{ fontSize: "13px", color: "#718096", fontWeight: "600" }}>
            Page {page} of {totalPages}
            <span style={{ marginLeft: "10px", color: "#2d3748" }}>({screenUsers.length} users)</span>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              style={{
                padding: "7px 14px", display: "flex", alignItems: "center", gap: "4px",
                backgroundColor: page === 1 ? "#e2e8f0" : "#fff",
                border: "1px solid #e2e8f0", borderRadius: "8px",
                cursor: page === 1 ? "not-allowed" : "pointer",
                fontWeight: "600", fontSize: "12px",
                color: page === 1 ? "#a0aec0" : "#2d3748",
              }}
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              style={{
                padding: "7px 14px", display: "flex", alignItems: "center", gap: "4px",
                backgroundColor: page === totalPages ? "#e2e8f0" : "#fff",
                border: "1px solid #e2e8f0", borderRadius: "8px",
                cursor: page === totalPages ? "not-allowed" : "pointer",
                fontWeight: "600", fontSize: "12px",
                color: page === totalPages ? "#a0aec0" : "#2d3748",
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </SectionCard>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
export default function LabBookingAnalytics() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [screenData, setScreenData] = useState([]);
  const [screenLoading, setScreenLoading] = useState(true);
  const [range] = useState("thisMonth");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setScreenLoading(true);

    // 1) Lab order analytics + all screen visit counts
    const [analyticsRes, screenRes] = await Promise.allSettled([
      fetch(`${API_BASE}/admin/lab/orders/analytics`).then((r) => r.json()),
      fetch(`${EVENTS_API}/screens?range=${range}`).then((r) => r.json()),
    ]);

    if (analyticsRes.status === "fulfilled") setAnalytics(analyticsRes.value.analytics || null);
    setLoading(false);

    const allScreens = screenRes.status === "fulfilled"
      ? (screenRes.value?.screens || screenRes.value?.data?.screens || [])
      : [];

    // 2) Fetch unique user count per lab screen via /users/by-screen (parallel)
    const labNames = LAB_SCREENS.map((s) => s.key);
    const userCountResults = await Promise.allSettled(
      labNames.map((name) =>
        fetch(`${EVENTS_API}/users/by-screen?screen=${encodeURIComponent(name)}&range=${range}`)
          .then((r) => r.json())
          .then((json) => ({ screen: name, totalUsers: json.data?.totalUsers || 0 }))
      )
    );

    const labOnly = labNames.map((name, idx) => {
      const fromScreens = allScreens.find((s) => s._id === name);
      const userRes = userCountResults[idx];
      const users = userRes.status === "fulfilled" ? userRes.value.totalUsers : 0;
      return {
        screen: name,
        totalVisits: fromScreens?.count || 0,
        uniqueUsers: users,
      };
    });
    setScreenData(labOnly);
    setScreenLoading(false);
  }, [range]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const byStatus = analytics?.byStatus || [];
  const byProvider = analytics?.byProvider || [];
  const dailyTrend = analytics?.dailyTrend || [];
  const topPincodes = (analytics?.topPincodes || []).filter((p) => p._id);
  const maxPincode = topPincodes[0]?.count || 0;
  const totalOrders = analytics?.total || 0;

  const weekChange = useMemo(() => {
    if (dailyTrend.length < 14) return null;
    const r7 = dailyTrend.slice(-7).reduce((s, d) => s + d.count, 0);
    const p7 = dailyTrend.slice(-14, -7).reduce((s, d) => s + d.count, 0);
    return p7 === 0 ? null : ((r7 - p7) / p7) * 100;
  }, [dailyTrend]);

  // Build funnel data from screen counts
  const funnelData = useMemo(() => {
    const maxVisits = screenData.length > 0 ? Math.max(...screenData.map((s) => s.totalVisits || 0), 1) : 1;
    return LAB_SCREENS.map((screen, idx) => {
      const d = screenData.find((s) => s.screen === screen.key);
      const visits = d?.totalVisits || 0;
      const prev = idx > 0 ? (screenData.find((s) => s.screen === LAB_SCREENS[idx - 1].key)?.totalVisits || 0) : 0;
      const dropPct = idx > 0 && prev > 0 ? (((prev - visits) / prev) * 100).toFixed(1) : null;
      return { ...screen, visits, users: d?.uniqueUsers || 0, maxVisits, dropPct };
    });
  }, [screenData]);

  return (
    <div style={{ minHeight: "100vh" }}>
      {/* Refresh */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "18px" }}>
        <button onClick={fetchAll} disabled={loading} style={{
          display: "flex", alignItems: "center", gap: "8px", padding: "10px 20px",
          background: "linear-gradient(135deg,#4f46e5,#7c3aed)", color: "#fff", border: "none",
          borderRadius: "12px", fontSize: "13px", fontWeight: "600",
          cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.6 : 1,
          boxShadow: "0 4px 12px rgba(79,70,229,0.25)", transition: "all 0.3s",
        }}>
          <RefreshCw size={15} className={loading ? "spin" : ""} />Refresh
        </button>
      </div>

      {/* ── ROW 1: KPIs ──────────────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "14px", marginBottom: "24px" }}>
        <KpiCard icon={FlaskConical} label="Total Orders" value={loading ? "..." : totalOrders.toLocaleString("en-IN")} sub={analytics ? `${inrShort(analytics.revenue)} revenue` : ""} color="#4f46e5" loading={loading} />
        <KpiCard icon={CreditCard} label="Paid" value={loading ? "..." : (analytics?.paid || 0).toLocaleString("en-IN")} sub={analytics ? `${inrShort(analytics.paidRevenue)} collected` : ""} color="#059669" loading={loading} />
        <KpiCard icon={Clock} label="Pending" value={loading ? "..." : (analytics?.pending || 0).toLocaleString("en-IN")} color="#d97706" loading={loading} />
        <KpiCard icon={CalendarCheck} label="Today" value={loading ? "..." : (analytics?.today?.count || 0).toLocaleString("en-IN")} sub={analytics ? inrShort(analytics.today?.revenue) : ""} color="#0891b2" loading={loading} />
        <KpiCard icon={TrendingUp} label="Last 7 Days" value={loading ? "..." : (analytics?.last7?.count || 0).toLocaleString("en-IN")} sub={analytics ? `${inrShort(analytics.last7?.revenue)}${weekChange !== null ? ` | ${weekChange >= 0 ? "+" : ""}${weekChange.toFixed(1)}% WoW` : ""}` : ""} color="#7c3aed" loading={loading} />
        <KpiCard icon={Activity} label="Last 30 Days" value={loading ? "..." : (analytics?.last30?.count || 0).toLocaleString("en-IN")} sub={analytics ? inrShort(analytics.last30?.revenue) : ""} color="#0284c7" loading={loading} />
      </div>

      {/* ── ROW 2: Trend + Funnel ────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "3fr 2fr", gap: "18px", marginBottom: "24px" }}>
        <SectionCard title="Daily Order Trend" icon={TrendingUp} color="#4f46e5">
          <TrendBars data={dailyTrend} loading={loading} />
        </SectionCard>

        <SectionCard title="Lab Booking Funnel" icon={Smartphone} color="#7c3aed">
          {screenLoading ? <Skeleton count={6} h={46} /> : (
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              {funnelData.map((f, i) => (
                <FunnelBar key={f.key} step={f.step} label={f.label} visits={f.visits} users={f.users} maxVisits={f.maxVisits} color={FUNNEL_COLORS[i] || "#4f46e5"} dropPct={f.dropPct} />
              ))}
            </div>
          )}
        </SectionCard>
      </div>

      {/* ── ROW 3: Status Donut + Partners & Pincodes ────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "18px", marginBottom: "24px" }}>
        {/* Status Donut */}
        <SectionCard title="Status Breakdown" icon={Package} color="#4f46e5">
          <StatusDonut data={byStatus} loading={loading} />
        </SectionCard>

        {/* Partners + Pincodes */}
        <SectionCard title="Partners & Regions" icon={MapPin} color="#7c3aed">
          {loading ? <Skeleton count={3} /> : (
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {byProvider.length === 0 ? <Empty text="No partner data" /> : byProvider.map((p) => (
                <ProviderRow key={p._id} provider={p._id} count={p.count} revenue={p.revenue} />
              ))}
              {topPincodes.length > 0 && (
                <>
                  <div style={{ fontSize: "11px", fontWeight: "700", color: "#718096", textTransform: "uppercase", letterSpacing: "0.06em", marginTop: "8px" }}>Top Pincodes</div>
                  {topPincodes.slice(0, 6).map((p, i) => (
                    <PincodeRow key={p._id} rank={i + 1} pincode={p._id} count={p.count} maxCount={maxPincode} />
                  ))}
                </>
              )}
            </div>
          )}
        </SectionCard>
      </div>

      {/* ── ROW 4: Orders by Status + Top Service Areas ──────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "18px", marginBottom: "24px" }}>
        <SectionCard title="Orders by Status" icon={Package} color="#4f46e5">
          {loading ? <Skeleton count={5} /> : byStatus.length === 0 ? <Empty text="No status data" /> : (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {byStatus.map((s) => <StatusRow key={s._id} status={s._id} count={s.count} revenue={s.revenue} total={totalOrders} />)}
            </div>
          )}
        </SectionCard>

        <SectionCard title="Top Service Areas" icon={MapPin} color="#7c3aed">
          {loading ? <Skeleton count={5} /> : topPincodes.length === 0 ? <Empty text="No pincode data" /> : (
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {topPincodes.map((p, i) => <PincodeRow key={p._id} rank={i + 1} pincode={p._id} count={p.count} maxCount={maxPincode} />)}
            </div>
          )}
        </SectionCard>
      </div>

      {/* ── ROW 5: Screen Users ──────────────────────────────────────────── */}
      <ScreenUsersSection range={range} />
    </div>
  );
}
